import React, {
    useState,
    useEffect,
} from 'react'
import { useTranslation } from 'react-i18next'
import Select, { SingleValue, MultiValue } from 'react-select'
import {
    Form,
    FormGroup,
    Label,
    Input,
    Button,
} from 'reactstrap'
import { withConnectWrapper } from '../../../validator'
import {
    readCompanies,
    readUsers,
} from '../../super-admin/users/helpers'
import AutocompleteRow, { MapRow } from '../../../components/shared/location-helper'
import {
    DEFAULT_LAT,
    DEFAULT_LNG,
} from '../../../components/shared/map'
import {
    readLocationsIP,
    saveDevice,
} from '../helpers'
import FormDescription from '../../../components/shared/wysiwyg/form-description'

interface SelectOption {
    value: string
    label: string
}

interface DeviceFormData {
    uid?: string
    name: string
    description: string
    latitude: number | string
    longitude: number | string
    formattedAddress: string
    managers: string[]
    companies: string[]
    locationIP: string
    isMonitored: boolean
    createCertificate: boolean
    logoWebPort: string
    [key: string]: unknown
}

interface DeviceFormOwnProps {
    updatedDevice?: DeviceFormData
    successAction?: () => void
    setEditedCompanyForm?: () => void
}

interface DeviceFormConnectedProps {
    companies: Array<{ uid: string; name: string }>
    users: Array<{ uid: string; name: string; surname: string }>
    locationsIP: Array<{ uid: string; name: string }>
}

type DeviceFormProps = DeviceFormOwnProps & DeviceFormConnectedProps

const initForm: DeviceFormData = {
    name: '',
    description: '',
    latitude: DEFAULT_LAT,
    longitude: DEFAULT_LNG,
    formattedAddress: '',
    managers: [],
    companies: [],
    locationIP: '',
    isMonitored: false,
    createCertificate: true,
    logoWebPort: '8888',
}

const logoWebPortOptions: SelectOption[] = [
    { value: '8080', label: '8080' },
    { value: '8888', label: '8888' },
]

/**
 * Form for creating and editing a device.
 *
 * @param {DeviceFormProps} props
 * @return {JSX.Element}
 */
function DeviceForm(props: DeviceFormProps): JSX.Element {
    const { t } = useTranslation()

    const {
        companies,
        users,
        locationsIP,
        updatedDevice,
        successAction,
    } = props

    const [companiesToSelect, setCompaniesToSelect] = useState<SelectOption[]>([])
    const [usersToSelect, setUsersToSelect] = useState<SelectOption[]>([])
    const [locationsIPToSelect, setLocationsIPToSelect] = useState<SelectOption[]>([])
    const [form, setForm] = useState<DeviceFormData>({ ...initForm })
    const [initFormDescription, setInitFormDescription] = useState<string>('')

    useEffect(() => {
        Promise
            .all([
                readUsers(),
                readCompanies(),
                readLocationsIP(),
            ])
            .then(() => {
                setCompaniesToSelect(
                    companies.map(company => ({
                        value: company.uid,
                        label: company.name,
                    })),
                )
                setUsersToSelect(
                    users.map(user => ({
                        value: user.uid,
                        label: `${user.name} ${user.surname}`,
                    })),
                )
                setLocationsIPToSelect(
                    locationsIP.map(locationIP => ({
                        value: locationIP.uid,
                        label: locationIP.name,
                    })),
                )
            })

        if (updatedDevice) {
            setForm({
                ...updatedDevice,
                logoWebPort: updatedDevice.logoWebPort || '8080',
            })
            setInitFormDescription(updatedDevice.description || '')
        }
    }, []) // eslint-disable-line react-hooks/exhaustive-deps

    /**
     * Set form value based on key
     *
     * @param {string} key
     * @param {unknown} value
     */
    const setFormData = <K extends keyof DeviceFormData>(key: K, value: DeviceFormData[K]): void => {
        setForm(prev => ({
            ...prev,
            [key]: value,
        }))
    }

    /**
     * Change location details when user interacts with map
     *
     * @param {google.maps.places.PlaceResult} place
     */
    const handleLocationChange = (place: google.maps.places.PlaceResult): void => {
        const lat = place?.geometry?.location?.lat?.()
        const lng = place?.geometry?.location?.lng?.()

        if (typeof lat !== 'number' || typeof lng !== 'number') {
            return
        }

        setForm(prev => ({
            ...prev,
            latitude: lat,
            longitude: lng,
            formattedAddress: place.formatted_address || '',
        }))
    }

    const {
        name,
        latitude,
        longitude,
        managers,
        companies: formCompanies,
        locationIP,
        isMonitored,
        logoWebPort,
    } = form

    return (
        <Form>
            <FormGroup>
                <Label for="device-form-name">
                    {t('device.form.name')}
                </Label>
                <Input
                    type="text"
                    name="name"
                    id="device-form-name"
                    placeholder={t('device.form.name')}
                    value={name || ''}
                    onChange={event => setFormData('name', event.target.value)}
                />
            </FormGroup>
            <FormGroup>
                <Label for="device-form-description">
                    {t('device.form.description')}
                </Label>
                <FormDescription
                    changeDescription={(content: string) => setFormData('description', content)}
                    description={initFormDescription || ''}
                    maxLength={10000}
                    classNames="p-2"
                />
            </FormGroup>
            <FormGroup>
                <AutocompleteRow
                    type="user_defined"
                    handleLocationChange={place => handleLocationChange(place)}
                />
            </FormGroup>
            <FormGroup>
                <Label for="device-form-latitude">
                    {t('device.form.latitude')}
                </Label>
                <Input
                    type="text"
                    name="name"
                    id="device-form-latitude"
                    placeholder={t('device.form.latitude')}
                    value={latitude || ''}
                    onChange={event => setFormData('latitude', event.target.value)}
                />
            </FormGroup>
            <FormGroup>
                <Label for="device-form-longitude">
                    {t('device.form.longitude')}
                </Label>
                <Input
                    type="text"
                    name="name"
                    id="device-form-longitude"
                    placeholder={t('device.form.longitude')}
                    value={longitude || ''}
                    onChange={event => setFormData('longitude', event.target.value)}
                />
            </FormGroup>
            <MapRow
                scrollwheel
                marker={{
                    lat: Number(latitude),
                    lng: Number(longitude),
                }}
                onMapClick={event => {
                    const latLng = event.detail.latLng

                    if (!latLng) {
                        return
                    }

                    setForm(prev => ({
                        ...prev,
                        latitude: latLng.lat,
                        longitude: latLng.lng,
                    }))
                }}
            />
            <FormGroup>
                <Label for="device-form-company">
                    {t('device.form.company')}
                </Label>
                <Select
                    value={formCompanies.map(company => companiesToSelect.find(companyToSelect => companyToSelect.value === company))}
                    isMulti
                    options={companiesToSelect}
                    onChange={(selectedOption: MultiValue<SelectOption | undefined>) =>
                        setFormData('companies', (selectedOption as SelectOption[]).map(option => option.value))
                    }
                    className="basic-multi-select"
                    classNamePrefix="select"
                    placeholder=""
                />
            </FormGroup>
            <FormGroup>
                <Label for="device-form-user">
                    {t('device.form.manager')}
                </Label>
                <Select
                    value={managers.map(manager => usersToSelect.find(managerToSelect => managerToSelect.value === manager))}
                    isMulti
                    options={usersToSelect}
                    onChange={(selectedOption: MultiValue<SelectOption | undefined>) =>
                        setFormData('managers', (selectedOption as SelectOption[]).map(option => option.value))
                    }
                    className="basic-multi-select"
                    classNamePrefix="select"
                    placeholder=""
                />
            </FormGroup>
            <FormGroup>
                <Label for="device-form-locationIP">
                    {t('device.form.locationIP')}
                </Label>
                <Select
                    value={locationsIPToSelect.find(locationIPToSelect => locationIPToSelect.value === locationIP)}
                    options={locationsIPToSelect}
                    onChange={(selectedOption: SingleValue<SelectOption>) =>
                        setFormData('locationIP', (selectedOption as SelectOption).value)
                    }
                    className="basic-single"
                    classNamePrefix="select"
                    placeholder=""
                />
            </FormGroup>
            <FormGroup>
                <Label for="device-form-logoWebPort">
                    {t('device.form.logoWebPort')}
                </Label>
                <Select
                    value={logoWebPortOptions.find(logoWebPortOption => logoWebPortOption.value === logoWebPort)}
                    options={logoWebPortOptions}
                    onChange={(selectedOption: SingleValue<SelectOption>) =>
                        setFormData('logoWebPort', (selectedOption as SelectOption).value)
                    }
                    className="basic-single"
                    classNamePrefix="select"
                    placeholder=""
                />
            </FormGroup>
            <FormGroup className="custom-control custom-checkbox">
                <Input
                    type="checkbox"
                    name="check"
                    className="custom-control-input"
                    id="device-form-is-monitored"
                    onChange={event => setFormData('isMonitored', event.target.checked)}
                    checked={isMonitored || false}
                />
                <Label
                    for="device-form-is-monitored"
                    className="custom-control-label"
                    check
                >
                    {t('device.form.isMonitored')}
                </Label>
            </FormGroup>
            <Button
                color="success"
                onClick={() => {
                    const deviceToSave = {
                        ...form,
                        latitude: form.latitude !== undefined && form.latitude !== '' ? Number(form.latitude) : undefined,
                        longitude: form.longitude !== undefined && form.longitude !== '' ? Number(form.longitude) : undefined,
                    }
                    saveDevice(deviceToSave)
                        .then(() => {
                            if (typeof successAction === 'function') {
                                successAction()
                            }
                            setForm({ ...initForm })
                            window.swalert(t('device.form.saveSuccess'), 'success')
                        })
                        .catch(error => window.swalert(error, 'danger'))
                }}
            >
                {t('device.form.save')}
            </Button>
        </Form>
    )
}

export default withConnectWrapper(state => ({
    companies: state.superAdmin.companies,
    users: state.superAdmin.users,
    locationsIP: state.superAdmin.locationsIP,
}))(DeviceForm as unknown as React.ComponentType<Record<string, unknown>>)
